import * as React from 'react';

// The board tabs, drawn from props only: the pinned game first, then the
// games opened beside it to compare. A tab shows the game's date and
// opponent; every tab but the pinned one has a close button. `index.tsx`
// reads the workspace and the game records; stories pass the data by hand.

export interface BoardTabItem {
  id: string;
  /** "Sep 12 · vs other"; empty while the game's record loads. */
  label: string;
  /** Longer text for the tooltip, e.g. both players and the result. */
  title?: string;
  pinned: boolean;
  /**
   * Set while something constrains this tab's board (a puzzle): the tab reads
   * its label in the accent, with a puzzle-piece icon; `detail` goes in its tooltip.
   */
  constraint?: { label: string; detail?: string };
}

export interface BoardTabsProps {
  tabs: BoardTabItem[];
  activeId: string;
  onActivate: (id: string) => void;
  onClose: (id: string) => void;
}

/** Phosphor "puzzle-piece", for a tab whose board is constrained. */
const PUZZLE =
  'M220.27,158.54a8,8,0,0,0-7.7-.46,20,20,0,1,1,0-36.16A8,8,0,0,0,224,114.69V72a16,16,0,0,0-16-16H171.78a35.36,35.36,0,0,0,.22-4,36.15,36.15,0,0,0-11.36-26.25,36,36,0,0,0-60.55,23.63,36.56,36.56,0,0,0,.14,6.62H64A16,16,0,0,0,48,72v32.22a35.36,35.36,0,0,0-4-.22,36.12,36.12,0,0,0-26.24,11.36,35.7,35.7,0,0,0-9.69,27,36.08,36.08,0,0,0,33.31,33.6,35.68,35.68,0,0,0,6.62-.14V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V165.31A8,8,0,0,0,220.27,158.54ZM208,208H64V165.31a8,8,0,0,0-11.43-7.23,20,20,0,1,1,0-36.16A8,8,0,0,0,64,114.69V72h46.69a8,8,0,0,0,7.23-11.43,20,20,0,1,1,36.16,0A8,8,0,0,0,161.31,72H208v32.23a35.68,35.68,0,0,0-6.62-.14A36,36,0,0,0,204,176a35.36,35.36,0,0,0,4-.22Z';

/** Phosphor "x", for the close buttons. */
const CLOSE =
  'M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z';

export const BoardTabs: React.FC<BoardTabsProps> = ({ tabs, activeId, onActivate, onClose }) => (
  <div className="board-tabs" role="tablist" aria-label="Open games">
    {tabs.map((tab) => {
      const active = tab.id === activeId;
      const constraint = tab.constraint;
      return (
        <div
          key={tab.id}
          className={`board-tab${active ? ' board-tab-active' : ''}${tab.pinned ? ' board-tab-pinned' : ''}${constraint ? ' board-tab-constrained' : ''}`}>
          <button
            type="button"
            role="tab"
            aria-selected={active}
            className="board-tab-label"
            data-track="review.boards.tab"
            title={constraint ? (constraint.detail ? `${constraint.label} · ${constraint.detail}` : constraint.label) : (tab.title ?? tab.label)}
            onClick={() => onActivate(tab.id)}>
            {constraint ? (
              <>
                <svg className="board-tab-icon" viewBox="0 0 256 256" width="12" height="12" fill="currentColor" aria-hidden="true">
                  <path d={PUZZLE} />
                </svg>
                {constraint.label}
              </>
            ) : (
              tab.label || '…'
            )}
          </button>
          {!tab.pinned && (
            <button
              type="button"
              className="board-tab-close"
              data-track="review.boards.close"
              aria-label={`Close ${tab.label || 'this game'}`}
              title="Close"
              onClick={() => onClose(tab.id)}>
              <svg viewBox="0 0 256 256" width="10" height="10" fill="currentColor" aria-hidden="true">
                <path d={CLOSE} />
              </svg>
            </button>
          )}
        </div>
      );
    })}
  </div>
);
